/**
 * Builds the homepage JSON-LD @graph from the same constants the page renders.
 *
 * Rules this module deliberately follows:
 *  - Every property is grounded in copy that is visible on the page.
 *  - No `sameAs` until real owned profiles exist and are linked visibly.
 *  - No `SearchAction`, because the public site has no site search.
 *  - No `offers`, `aggregateRating`, `review`, `award` or `foundingDate`.
 *    None of those are verifiable facts today. `legalName` and the town-level
 *    `address` match the operator line in the footer, contact page and Terms.
 *  - No `logo`, because no first-party logo raster asset exists yet.
 *  - `alternateName` and `slogan` carry the domain and the visible footer
 *    descriptor, to separate this product from similarly named tools.
 *
 * Nodes are cross-linked by `@id` so the graph reads as one connected entity
 * rather than four unrelated blobs.
 */

import { SITE, SITE_URL, PRODUCT_DEFINITION, FAQS } from "@/content/site";

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const SOFTWARE_ID = `${SITE_URL}/#software`;
export const FAQ_ID = `${SITE_URL}/#faq`;

export function buildHomepageGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: SITE.name,
        alternateName: SITE.domain,
        slogan: SITE.descriptor,
        legalName: SITE.operator,
        address: { "@type": "PostalAddress", addressLocality: SITE.locality, addressCountry: SITE.countryCode },
        url: SITE.url,
        email: SITE.contactEmail,
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: SITE.name,
        url: SITE.url,
        description: SITE.description,
        inLanguage: "en-GB",
        publisher: { "@id": ORGANIZATION_ID },
      },
      {
        "@type": "SoftwareApplication",
        "@id": SOFTWARE_ID,
        name: SITE.name,
        alternateName: SITE.domain,
        url: SITE.url,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: PRODUCT_DEFINITION,
        publisher: { "@id": ORGANIZATION_ID },
        isPartOf: { "@id": WEBSITE_ID },
      },
      {
        "@type": "FAQPage",
        "@id": FAQ_ID,
        isPartOf: { "@id": WEBSITE_ID },
        mainEntity: FAQS.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ],
  };
}

/**
 * Serialised for direct injection into a script tag. `<` is escaped so the
 * payload cannot terminate the surrounding script element early.
 */
export function buildHomepageGraphJson() {
  return JSON.stringify(buildHomepageGraph()).replace(/</g, "\\u003c");
}
